// Libs
import classNames from 'classnames/bind';
import {
  Checkbox,
  CheckboxGroup,
  FileDrop,
  Flex,
  IconModuleLine,
  NumberInput,
  RadioInput,
  RadioInputGroup,
  Spinner,
  Text,
  TextInput,
  View,
} from '@instructure/ui';
import ReactQuill from 'react-quill';
import { useTranslation } from 'react-i18next';
// Components, Layouts, Pages
import { BaseSelect } from '~/components/common';
// Others
import { IQuizQuestion } from '~/utils/interface/quiz';
import { QuizQuestionTypeEnum } from '~/utils/enum/common';
import { IBaseSelectOption } from '~/utils/interface/common';
// Styles, images, icons
import styles from './QuestionItem.module.scss';
import 'react-quill/dist/quill.snow.css';

type Props = {
  question: IQuizQuestion;
  isLoading?: boolean;
  onChangeAnswer?: (questionId: string, answer: any, save?: boolean) => void;
  onChangeFileAnswer?: (questionId: string, file: File) => void;
};

const cx = classNames.bind(styles);

const QuestionItemComponent = (props: Props) => {
  //#region Destructuring Props
  const { question, isLoading = false, onChangeAnswer, onChangeFileAnswer } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['common/quiz', 'common/base']);
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  //#endregion Implement Hook

  //#region Handle Function
  const renderAnswers = () => {
    if (
      question.questionType === QuizQuestionTypeEnum.MULTIPLE_CHOICE ||
      question.questionType === QuizQuestionTypeEnum.TRUE_FALSE
    ) {
      return (
        <RadioInputGroup
          onChange={(_, value) => onChangeAnswer?.(question.id, value, true)}
          name={`question_${question.id}`}
          description=''
          value={question.answer ? question.answer.toString() : undefined}
        >
          {question.answers.map((answer) => {
            return (
              <RadioInput
                key={answer.id}
                checked={+question.answer === +answer.id}
                size='small'
                value={answer.id.toString()}
                label={answer.text}
              />
            );
          })}
        </RadioInputGroup>
      );
    }

    if (question.questionType === QuizQuestionTypeEnum.SHORT_ANSWER) {
      return (
        <TextInput
          key={`question_${question.id}`}
          value={question.answer}
          onChange={(_, value) => onChangeAnswer?.(question.id, value, false)}
          onBlur={(event) => onChangeAnswer?.(question.id, event.target.value, true)}
        />
      );
    }

    if (question.questionType === QuizQuestionTypeEnum.MULTIPLE_ANSWERS) {
      const selectedOptions = Array.isArray(question.answer) ? question.answer.map((answer) => answer.toString()) : [];
      return (
        <CheckboxGroup
          name={`question_${question.id}`}
          onChange={(values) => {
            const updateAnswer = values.map((value) => +value);
            onChangeAnswer?.(question.id, updateAnswer, true);
          }}
          description=''
          defaultValue={selectedOptions}
        >
          {question.answers.map((answer) => {
            const isChecked = selectedOptions.find((option) => +option === +answer.id) ? true : false;
            return <Checkbox key={answer.id} checked={isChecked} label={answer.text} value={answer.id.toString()} />;
          })}
        </CheckboxGroup>
      );
    }

    if (question.questionType === QuizQuestionTypeEnum.MATCHING) {
      const options: IBaseSelectOption[] =
        question.matches?.map((match) => ({
          value: match.matchId,
          label: match.text,
        })) ?? [];

      return (
        <div className={cx('matchingContainer')}>
          {question.answers.map((answer) => {
            const valueSelected = Array.isArray(question.answer)
              ? question.answer.find((value) => +value.answer_id === +answer.id)?.match_id
              : undefined;
            return (
              <div key={answer.id} className={cx('matchingItem')}>
                <View as='div' width={'40%'}>
                  <Text>{answer.text}</Text>
                </View>

                <View as='div' width={'40%'}>
                  <BaseSelect
                    onChange={(data) => {
                      let updateAnswer = (Array.isArray(question.answer) ? question.answer : []).filter(
                        (item) => +item.answer_id !== +answer.id
                      );
                      updateAnswer.push({ answer_id: +answer.id, match_id: data.value });
                      onChangeAnswer?.(question.id, updateAnswer, true);
                    }}
                    value={valueSelected}
                    options={options}
                  />
                </View>
              </div>
            );
          })}
        </div>
      );
    }

    if (
      question.questionType === QuizQuestionTypeEnum.NUMERICAL ||
      question.questionType === QuizQuestionTypeEnum.CALCULATED
    ) {
      return (
        <NumberInput
          key={`question_${question.id}`}
          onChange={(_, value) => onChangeAnswer?.(question.id, value ? +value : null, false)}
          onBlur={(event) => onChangeAnswer?.(question.id, event.target.value ? +event.target.value : null, true)}
          value={question.answer}
          renderLabel=''
          showArrows={false}
        />
      );
    }

    if (question.questionType === QuizQuestionTypeEnum.ESSAY) {
      return (
        <ReactQuill
          key={`question_${question.id}`}
          value={question.answer}
          onChange={(value) => onChangeAnswer?.(question.id, value, false)}
          onBlur={(previousSelection, source, editor) => onChangeAnswer?.(question.id, editor.getHTML(), true)}
          theme='snow'
        />
      );
    }

    if (question.questionType === QuizQuestionTypeEnum.FILE_UPLOAD) {
      return (
        <FileDrop
          key={`question_${question.id}`}
          onDropAccepted={(files) => {
            const fileList = Array.from(files) as File[];
            onChangeFileAnswer?.(question.id, fileList[0]);
          }}
          renderLabel={
            <View as='div' padding='xx-large large' background='primary'>
              <Flex direction='column' alignItems='center' gap='small'>
                <IconModuleLine size='large' />
                <Text>{t('quiz_drag_drop_question_label')}</Text>

                {question.answer && (
                  <Text color='brand' size='small'>
                    {question.answer}
                  </Text>
                )}
              </Flex>
            </View>
          }
        />
      );
    }

    return <></>;
  };
  //#endregion Handle Function

  return (
    <div id={`question-${question.id}`} className={cx('container')}>
      <div className={cx('header')}>
        <Text size='medium' weight='bold'>
          {question.questionName}
        </Text>
      </div>

      <div className={cx('description')} dangerouslySetInnerHTML={{ __html: question.questionText }} />

      <div className={cx('answerContainer')}>{renderAnswers()}</div>

      {isLoading && (
        <div className={cx('loadingContainer')}>
          <Spinner size='small' />
        </div>
      )}
    </div>
  );
};

export default QuestionItemComponent;
